import { SavedViewsDropdown } from '@/features/views/components/SavedViewsDropdown';
import { CreateWorkItemModal } from '@/features/work-items/components/CreateWorkItemModal';
import { KanbanView } from '@/features/work-items/views/KanbanView';
import { ViewSwitcher } from '@/features/work-items/views/ViewSwitcher';
import { useQuickCreate } from '@/lib/hooks/useQuickCreate';
import { getProjectBySlugQuery } from '@/lib/supabase/queries/projects';
import { getWorkspaceBySlugQuery } from '@/lib/supabase/queries/workspaces';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { FileText, Settings } from 'lucide-react';
import { useState } from 'react';

export const Route = createFileRoute('/_app/$wsSlug/projects/$projectSlug/board')({
  component: BoardPage,
});

function BoardPage() {
  const { wsSlug, projectSlug } = useParams({
    from: '/_app/$wsSlug/projects/$projectSlug/board',
  });
  const navigate = useNavigate();

  const wsQ = useQuery(getWorkspaceBySlugQuery(wsSlug));
  const projQ = useQuery({
    ...getProjectBySlugQuery(wsQ.data?.id ?? '', projectSlug),
    enabled: !!wsQ.data?.id,
  });

  const [createOpen, setCreateOpen] = useState(false);
  useQuickCreate(() => setCreateOpen(true));

  if (wsQ.isPending || (wsQ.data && projQ.isPending)) {
    return <div className="p-6 text-sm text-quiet">Cargando proyecto...</div>;
  }
  if (!wsQ.data || !projQ.data) {
    return (
      <div className="p-6 text-sm text-quiet">
        Proyecto no encontrado o sin permisos para verlo.
      </div>
    );
  }
  const ws = wsQ.data;
  const project = projQ.data;

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-3 pt-3 pb-2 border-b border-border-soft">
        <div>
          <h2 className="text-lg font-semibold text-ink">{project.name}</h2>
          <p className="text-sm text-quiet mt-0.5">
            Tablero Kanban. Arrastra tarjetas entre columnas para cambiar el estado de una tarea.
            Activa "Mostrar sub-tareas" para ver los hijos junto a sus padres.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <SavedViewsDropdown projectId={project.id} workspaceId={ws.id} currentLayout="kanban" />
          <ViewSwitcher
            value="kanban"
            onChange={(next) => {
              if (next === 'list') {
                navigate({
                  to: '/$wsSlug/projects/$projectSlug/list',
                  params: { wsSlug, projectSlug },
                });
              } else if (next === 'gantt') {
                navigate({
                  to: '/$wsSlug/projects/$projectSlug/gantt',
                  params: { wsSlug, projectSlug },
                });
              }
            }}
          />
          {project.kind !== 'todo' && (
            <Link
              to="/$wsSlug/projects/$projectSlug/minuta"
              params={{ wsSlug, projectSlug }}
              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs text-quiet hover:text-ink hover:bg-accent-sf"
              title="Crear tareas desde minuta"
            >
              <FileText size={14} />
              <span>Crear desde minuta</span>
            </Link>
          )}
          <Link
            to="/$wsSlug/projects/$projectSlug/settings"
            params={{ wsSlug, projectSlug }}
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs text-quiet hover:text-ink hover:bg-accent-sf"
            title="Configuracion del proyecto"
          >
            <Settings size={14} />
          </Link>
        </div>
      </div>
      <div className="flex-1 min-h-0">
        <KanbanView
          projectId={project.id}
          workspaceId={ws.id}
          projectIdentifier={project.identifier}
          dueWarningDays={project.due_warning_days ?? 3}
          onOpenItem={(id) =>
            navigate({
              to: '/$wsSlug/projects/$projectSlug/$itemId',
              params: { wsSlug, projectSlug, itemId: id },
            })
          }
        />
      </div>
      <CreateWorkItemModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        workspaceId={ws.id}
        projectId={project.id}
      />
    </div>
  );
}
